import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Search, Globe, Home, ClipboardList, MessageCircle } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import SheepIcon from './SheepIcon';

export default function Header({ onSearchChange, searchValue }) {
  const { t, lang, setLang, cart, whatsappNumber } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [focused, setFocused] = useState(false);

  const isHome     = location.pathname === '/';
  const isOrders   = location.pathname === '/orders';

  const NavBtn = ({ label, icon: Icon, onClick, active }) => (
    <button
      onClick={onClick}
      aria-label={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: active ? 'rgba(255,255,255,0.20)' : 'transparent',
        border: '1.5px solid',
        borderColor: active ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.18)',
        borderRadius: 'var(--r-full)',
        padding: '7px 15px',
        color: active ? '#fff' : 'rgba(255,255,255,0.80)',
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        transition: 'all var(--ease)',
        fontFamily: 'inherit',
      }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.80)'; } }}
    >
      <Icon size={14} />
      {label}
    </button>
  );

  return (
    <header
      style={{
        background: 'linear-gradient(160deg, #5C0B0B 0%, #7A1010 45%, #8E1414 100%)',
        boxShadow: '0 2px 16px rgba(92,11,11,0.35)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          padding: '14px 16px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: 1800,
          margin: '0 auto',
          gap: 12,
        }}
      >
        {/* Brand */}
        <button
          onClick={() => navigate('/')}
          aria-label="Home"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0,
          }}
        >
          {/* Logo circle */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.12)',
              border: '1.5px solid rgba(255,255,255,0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SheepIcon size={28} color="#fff" opacity={0.95} />
          </div>

          {/* Brand name */}
          <div style={{ textAlign: lang === 'ar' ? 'right' : 'left' }}>
            <div
              style={{
                color: '#fff',
                fontWeight: 800,
                fontSize: 17,
                lineHeight: 1.2,
                letterSpacing: lang === 'ar' ? 0 : '-0.3px',
              }}
            >
              {lang === 'ar' ? 'بن غانم للمواشي' : 'Bin Ghanim Livestock'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.60)', fontSize: 11, marginTop: 1 }}>
              {lang === 'ar' ? 'أفضل الأغنام الأصيلة' : 'Premium Livestock · GCC'}
            </div>
          </div>
        </button>

        {/* Right actions */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {/* Desktop nav */}
          <div className="desktop-nav">
            <NavBtn label={t.home}     icon={Home}         onClick={() => navigate('/')}        active={isHome}   />
            <NavBtn label={t.myOrders} icon={ClipboardList} onClick={() => navigate('/orders')}  active={isOrders} />
            {whatsappNumber && (
              <NavBtn
                label={lang === 'ar' ? 'تواصل' : 'Contact'}
                icon={MessageCircle}
                onClick={() => window.open(`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`, '_blank')}
                active={false}
              />
            )}
          </div>

          {/* Language toggle */}
          <button
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            aria-label="Toggle language"
            style={{
              background: 'rgba(255,255,255,0.12)',
              border: '1.5px solid rgba(255,255,255,0.22)',
              borderRadius: 'var(--r-full)',
              padding: '5px 11px',
              color: 'rgba(255,255,255,0.88)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              transition: 'all var(--ease)',
              fontFamily: 'inherit',
            }}
          >
            <Globe size={12} />
            {lang === 'ar' ? 'EN' : 'عربي'}
          </button>

          {/* Cart button */}
          <button
            onClick={() => navigate('/cart')}
            aria-label={`${t.cart}${cart.length > 0 ? ` (${cart.length})` : ''}`}
            style={{
              background: cart.length > 0 ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.12)',
              border: '1.5px solid rgba(255,255,255,0.28)',
              borderRadius: 'var(--r-full)',
              padding: '7px 14px',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              transition: 'all var(--ease)',
              fontFamily: 'inherit',
            }}
          >
            <ShoppingCart size={17} strokeWidth={2} />
            {cart.length > 0 && (
              <span
                style={{
                  background: '#22C55E',
                  color: '#fff',
                  borderRadius: 'var(--r-full)',
                  minWidth: 18,
                  height: 18,
                  padding: '0 4px',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cart.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div style={{ padding: '4px 16px 18px', maxWidth: 1800, margin: '0 auto' }}>
        <div
          style={{
            background: focused ? '#fff' : 'rgba(255,255,255,0.95)',
            borderRadius: 'var(--r-full)',
            display: 'flex',
            alignItems: 'center',
            padding: '10px 18px',
            gap: 10,
            boxShadow: focused
              ? '0 0 0 3px rgba(255,255,255,0.30), 0 4px 16px rgba(0,0,0,0.15)'
              : '0 2px 8px rgba(0,0,0,0.12)',
            transition: 'box-shadow 0.2s, background 0.2s',
          }}
        >
          <Search size={17} color={focused ? '#7A1010' : '#A8A29E'} style={{ flexShrink: 0, transition: 'color 0.2s' }} />
          <input
            value={searchValue}
            onChange={e => onSearchChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={t.search}
            aria-label={t.search}
            style={{
              border: 'none',
              outline: 'none',
              flex: 1,
              fontSize: 15,
              fontFamily: 'inherit',
              background: 'transparent',
              color: '#1C1917',
              direction: lang === 'ar' ? 'rtl' : 'ltr',
            }}
          />
          {searchValue && (
            <button
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              style={{
                background: '#E7E5E4',
                border: 'none',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#78716C',
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
